import { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  SUBMIT_MODE_LABELS,
  SUBMIT_MODES,
  addDays,
  formatLongDate,
  formatRange,
  formatVisibleFrom,
  fromDatetimeLocalValue,
  offsetIsoAt,
  parseDateKey,
  toDateKey,
  toDatetimeLocalValue,
  type LessonDetail,
  type LessonUpdateInput,
  type SubmitMode,
} from '@skolveckan/shared';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { SaveTemplateDialog } from '../components/SaveTemplateDialog';
import { IconChevronLeft, IconEye, IconInfo, IconPlus } from '../components/Icons';
import { LessonContent } from '../components/LessonContent';
import { useApi } from '../hooks/useApi';
import { useUnsavedChanges } from '../hooks/useUnsavedChanges';
import { ApiError, api } from '../lib/api';

/**
 * Formuläret plus en levande förhandsvisning av elevens vy. Förhandsvisningen
 * renderar samma LessonContent som modalen — inte en kopia.
 */
export function AdminLessonPage() {
  const { id = '' } = useParams();
  const [searchParams] = useSearchParams();
  const templateId = searchParams.get('mall');

  const loaded = useApi(() => api.lesson(id), [id]);

  // Basen är lektionen som den ligger på servern. Den uppdateras vid varje
  // lyckad sparning, så att förhandsvisningen och "ej sparad" utgår från det
  // som faktiskt finns — inte från hur lektionen såg ut när sidan öppnades.
  const [base, setBase] = useState<LessonDetail | null>(null);
  const [form, setForm] = useState<LessonUpdateInput | null>(null);
  const [visChoice, setVisChoice] = useState<VisibilityChoice>('direct');
  const [errors, setErrors] = useState<FormErrors>(noErrors);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [note, setNote] = useState<Note | null>(null);
  const [templateOpen, setTemplateOpen] = useState(false);
  const [savingTemplate, setSavingTemplate] = useState(false);

  const blocker = useUnsavedChanges(dirty);

  useEffect(() => {
    if (!loaded.data) return;
    setBase(loaded.data);
    setForm(toForm(loaded.data));
    setVisChoice(choiceFor(loaded.data.visibleFrom, loaded.data));
    setErrors(noErrors);
    setDirty(false);
  }, [loaded.data]);

  // Applicera mallen som valdes i schemarastret, som förhandsvisning.
  useEffect(() => {
    if (!templateId || !loaded.data) return;
    void api.applyTemplate(id, templateId, { overwrite: false }).then((res) => {
      setForm(toForm(res.preview));
      setDirty(true);
      setNote({ kind: 'info', text: 'Mallen är ifylld men inte sparad än.' });
    });
  }, [templateId, loaded.data, id]);

  const preview = useMemo<LessonDetail | null>(() => {
    if (!base || !form) return null;
    // Samma städning som vid sparning. Annars ritar förhandsvisningen en tom
    // rad så fort någon klickat "Lägg till länk" — och påstår därmed att eleven
    // ser något som aldrig kommer att sparas.
    const shown = cleaned(form);
    return {
      ...base,
      title: shown.title || null,
      steps: shown.steps.map((text, i) => ({ id: `p${i}`, position: i * 10, text })),
      helps: shown.helps.map((text, i) => ({ id: `h${i}`, position: i * 10, text })),
      links: shown.links.map((link, i) => ({
        id: `l${i}`,
        position: i * 10,
        title: link.title,
        url: link.url,
        sourceLabel: link.sourceLabel ?? null,
      })),
      book: {
        title: shown.book.title || null,
        pages: shown.book.pages || null,
        exercises: shown.book.exercises || null,
      },
      handInNote: shown.handInNote || null,
      submitMode: shown.submitMode,
      visibleFrom: shown.visibleFrom,
      chatEnabled: shown.chatEnabled,
      chatMode: shown.chatMode,
    };
  }, [base, form]);

  if (loaded.loading || !form || !base) {
    return <p className="muted">Hämtar lektionen…</p>;
  }

  const lesson = base;

  function update(patch: Partial<LessonUpdateInput>) {
    setForm((prev) => (prev ? { ...prev, ...patch } : prev));
    setDirty(true);
    setNote(null);
  }

  function chooseVisibility(choice: VisibilityChoice) {
    setVisChoice(choice);
    if (choice === 'custom') {
      // Behåll ett redan valt klockslag, annars börja på något rimligt i
      // stället för ett tomt fält som inte går att spara.
      update({ visibleFrom: form?.visibleFrom ?? presets(lesson).day_before });
      return;
    }
    update({ visibleFrom: presets(lesson)[choice] });
  }

  async function save(status: 'draft' | 'published') {
    if (!form) return;
    const found = validate(form, status);
    setErrors(found);
    if (hasErrors(found)) {
      setNote({ kind: 'error', text: 'Något i formuläret behöver rättas först.' });
      return;
    }

    setSaving(true);
    setNote(null);
    try {
      const updated = await api.saveLesson(id, { ...cleaned(form), status });
      setBase(updated);
      setForm(toForm(updated));
      setVisChoice(choiceFor(updated.visibleFrom, updated));
      setDirty(false);
      setNote({
        kind: 'ok',
        text:
          status === 'published'
            ? 'Sparad och publicerad för klassen.'
            : 'Sparad som utkast. Eleverna ser den inte än.',
      });
    } catch (err) {
      setNote({
        kind: 'error',
        text:
          err instanceof ApiError
            ? err.message
            : 'Kunde inte spara. Kontrollera nätet och försök igen.',
      });
    } finally {
      setSaving(false);
    }
  }

  async function saveTemplate(name: string, description: string) {
    if (!form) return;
    const shown = cleaned(form);
    setSavingTemplate(true);
    setNote(null);
    try {
      // Innehållet tas från formuläret och inte från servern, så att osparade
      // ändringar kommer med — det är det som står på skärmen läraren menar.
      await api.createTemplate({
        name,
        description,
        subjectId: lesson.subject?.id ?? null,
        payload: {
          title: shown.title,
          steps: shown.steps,
          helps: shown.helps,
          links: shown.links.map((l) => ({
            title: l.title,
            url: l.url,
            sourceLabel: l.sourceLabel ?? null,
          })),
          bookTitle: shown.book.title,
          bookPages: shown.book.pages,
          bookExercises: shown.book.exercises,
          submitMode: shown.submitMode,
          handInNote: shown.handInNote,
          chatEnabled: shown.chatEnabled,
          chatMode: shown.chatMode,
        },
      });
      setTemplateOpen(false);
      setNote({ kind: 'ok', text: `Sparad som mallen ”${name}”.` });
    } catch (err) {
      setNote({
        kind: 'error',
        text: err instanceof ApiError ? err.message : 'Kunde inte spara mallen.',
      });
    } finally {
      setSavingTemplate(false);
    }
  }

  const visibleAt = form.visibleFrom;

  return (
    <>
      <header className="page__head">
        <Link to="/admin" className="button button--ghost button--icon" aria-label="Tillbaka">
          <IconChevronLeft />
        </Link>
        <h1>Lektionsinnehåll</h1>
        {dirty && <span className="chip chip--warn">Ej sparad</span>}
        {!dirty && lesson.status === 'published' && (
          <span className="chip chip--accent">Publicerad</span>
        )}
        {!dirty && lesson.status !== 'published' && <span className="chip">Utkast</span>}
        <div className="grow" />
        <button
          type="button"
          className="button button--ghost"
          onClick={() => setTemplateOpen(true)}
        >
          Spara som mall
        </button>
        <button
          type="button"
          className="button button--ghost"
          disabled={saving}
          onClick={() => void save('draft')}
        >
          Spara utkast
        </button>
        <button
          type="button"
          className="button button--primary"
          disabled={saving}
          onClick={() => void save('published')}
        >
          {saving ? 'Sparar…' : 'Spara och publicera'}
        </button>
      </header>

      <p className="muted" style={{ marginTop: -8 }}>
        {lesson.subject?.name} · {capitalize(formatLongDate(lesson.date))} ·{' '}
        {formatRange(lesson.startMin, lesson.endMin)}
        {lesson.room ? ` · ${lesson.room}` : ''}
      </p>

      {note && (
        <p className={`formnote formnote--${note.kind}`} role="status">
          {note.text}
        </p>
      )}

      <div className="admin__columns admin__columns--form">
        <section className="card panel form">
          <div className="field">
            <label htmlFor="title">Rubrik som eleven ser</label>
            <input
              id="title"
              type="text"
              value={form.title}
              onChange={(e) => update({ title: e.target.value })}
            />
          </div>

          <RepeatableList
            idPrefix="step"
            legend="Så här jobbar ni – ett steg per rad"
            values={form.steps}
            placeholder="Nytt steg…"
            numbered
            error={errors.steps}
            onChange={(steps) => update({ steps })}
          />

          <div className="formrow">
            <div className="field grow">
              <label htmlFor="book">Bok</label>
              <input
                id="book"
                type="text"
                value={form.book.title}
                onChange={(e) => update({ book: { ...form.book, title: e.target.value } })}
              />
            </div>
            <div className="field" style={{ width: 120 }}>
              <label htmlFor="pages">Sidor</label>
              <input
                id="pages"
                type="text"
                value={form.book.pages}
                onChange={(e) => update({ book: { ...form.book, pages: e.target.value } })}
              />
            </div>
            <div className="field" style={{ width: 200 }}>
              <label htmlFor="exercises">Uppgifter</label>
              <input
                id="exercises"
                type="text"
                value={form.book.exercises}
                onChange={(e) => update({ book: { ...form.book, exercises: e.target.value } })}
              />
            </div>
          </div>

          <RepeatableList
            idPrefix="help"
            legend="Hjälpavsnitt"
            values={form.helps}
            placeholder="Till exempel: repetition s. 80–81"
            onChange={(helps) => update({ helps })}
          />

          <fieldset className="fieldset">
            <legend className="label">Länkar till hjälp på nätet</legend>
            {form.links.map((link, i) => {
              const error = errors.links[i];
              return (
                <div className="stack" key={i} style={{ gap: 4 }}>
                  <div className="formrow">
                    <label className="visually-hidden" htmlFor={`link-title-${i}`}>
                      Länktitel {i + 1}
                    </label>
                    <input
                      id={`link-title-${i}`}
                      type="text"
                      style={{ width: 220 }}
                      value={link.title}
                      placeholder="Titel"
                      onChange={(e) => {
                        const links = [...form.links];
                        links[i] = { ...link, title: e.target.value };
                        update({ links });
                      }}
                    />
                    <label className="visually-hidden" htmlFor={`link-url-${i}`}>
                      Länkadress {i + 1}
                    </label>
                    <input
                      id={`link-url-${i}`}
                      type="url"
                      className="grow"
                      value={link.url}
                      placeholder="https://"
                      aria-invalid={error ? true : undefined}
                      aria-describedby={error ? `link-error-${i}` : undefined}
                      onChange={(e) => {
                        const links = [...form.links];
                        links[i] = { ...link, url: e.target.value };
                        update({ links });
                      }}
                    />
                    <button
                      type="button"
                      className="iconbutton"
                      aria-label={`Ta bort länk ${i + 1}`}
                      onClick={() => update({ links: form.links.filter((_, j) => j !== i) })}
                    >
                      ×
                    </button>
                  </div>
                  {error && (
                    <p className="field__error" id={`link-error-${i}`}>
                      {error}
                    </p>
                  )}
                </div>
              );
            })}
            <button
              type="button"
              className="addrow"
              onClick={() => update({ links: [...form.links, { title: '', url: '' }] })}
            >
              <IconPlus size={12} />
              Lägg till länk
            </button>
          </fieldset>

          <div className="formrow">
            <div className="field grow">
              <label htmlFor="submit-mode">Inlämning</label>
              <select
                id="submit-mode"
                value={form.submitMode}
                onChange={(e) => update({ submitMode: e.target.value as SubmitMode })}
              >
                {SUBMIT_MODES.map((mode) => (
                  <option key={mode} value={mode}>
                    {SUBMIT_MODE_LABELS[mode]}
                  </option>
                ))}
              </select>
            </div>
            <div className="field grow">
              <label htmlFor="visible">Synlig för eleven</label>
              <select
                id="visible"
                value={visChoice}
                onChange={(e) => chooseVisibility(e.target.value as VisibilityChoice)}
              >
                <option value="day_before">Dagen innan kl. 17:00</option>
                <option value="lesson_start">När lektionen börjar</option>
                <option value="direct">Direkt</option>
                <option value="custom">Eget datum och tid…</option>
              </select>
            </div>
          </div>

          {visChoice === 'custom' && (
            <div className="field" style={{ maxWidth: 260 }}>
              <label htmlFor="visible-at">Synlig från</label>
              <input
                id="visible-at"
                type="datetime-local"
                value={visibleAt ? toDatetimeLocalValue(visibleAt) : ''}
                onChange={(e) =>
                  update({ visibleFrom: fromDatetimeLocalValue(e.target.value) })
                }
              />
            </div>
          )}

          <div className="field">
            <label htmlFor="handin">Anteckning om inlämning</label>
            <input
              id="handin"
              type="text"
              value={form.handInNote}
              onChange={(e) => update({ handInNote: e.target.value })}
            />
          </div>

          <label className="togglerow">
            <input
              type="checkbox"
              checked={form.chatEnabled}
              onChange={(e) => update({ chatEnabled: e.target.checked })}
            />
            Chatt på för den här lektionen
            <span className="grow" />
            <select
              aria-label="Chattens läge"
              value={form.chatMode}
              disabled={!form.chatEnabled}
              onChange={(e) => update({ chatMode: e.target.value as 'hints' | 'free' })}
            >
              <option value="hints">Ledtrådar, inte färdiga svar</option>
              <option value="free">Förklara fritt</option>
            </select>
          </label>
        </section>

        <div className="page__aside">
          <span className="label row" style={{ gap: 8 }}>
            <IconEye />
            Så här ser det ut för eleven
          </span>
          <section className="card panel preview">
            {preview && <LessonContent lesson={preview} compact />}
          </section>
          <section className="card infocard">
            <IconInfo />
            <span>
              {visibleAt
                ? `Blir synlig för klassen ${formatVisibleFrom(visibleAt)}, när lektionen är publicerad.`
                : 'Blir synlig för klassen så snart du publicerar. Eleverna ser inte utkast.'}
            </span>
          </section>
        </div>
      </div>

      <SaveTemplateDialog
        open={templateOpen}
        suggestedName={form.title.trim() || lesson.subject?.name || 'Ny mall'}
        summary={`${cleaned(form).steps.length} steg, ${cleaned(form).links.length} länkar. Mallen tar det som står i formuläret nu, även osparat.`}
        saving={savingTemplate}
        onCancel={() => setTemplateOpen(false)}
        onSave={(name, description) => void saveTemplate(name, description)}
      />

      <ConfirmDialog
        open={blocker.state === 'blocked'}
        title="Du har ändringar som inte är sparade"
        body="Lämnar du sidan nu försvinner det du skrivit sedan senaste sparningen."
        cancelLabel="Stanna kvar"
        confirmLabel="Lämna utan att spara"
        onCancel={() => blocker.reset?.()}
        onConfirm={() => blocker.proceed?.()}
      />
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Synlighet                                                           */
/* ------------------------------------------------------------------ */

type VisibilityChoice = 'day_before' | 'lesson_start' | 'direct' | 'custom';

/** Dagen innan, kl. 17:00 — när eleverna oftast tittar inför morgondagen. */
const DAY_BEFORE_MIN = 17 * 60;

function presets(lesson: LessonDetail): Record<'day_before' | 'lesson_start' | 'direct', string | null> {
  const dayBefore = toDateKey(addDays(parseDateKey(lesson.date), -1));
  return {
    day_before: offsetIsoAt(dayBefore, DAY_BEFORE_MIN),
    lesson_start: offsetIsoAt(lesson.date, lesson.startMin),
    direct: null,
  };
}

function choiceFor(visibleFrom: string | null, lesson: LessonDetail): VisibilityChoice {
  if (!visibleFrom) return 'direct';
  const p = presets(lesson);
  if (visibleFrom === p.day_before) return 'day_before';
  if (visibleFrom === p.lesson_start) return 'lesson_start';
  return 'custom';
}

/* ------------------------------------------------------------------ */
/* Validering                                                          */
/* ------------------------------------------------------------------ */

interface FormErrors {
  steps?: string;
  links: Record<number, string>;
}

const noErrors: FormErrors = { links: {} };

function hasErrors(e: FormErrors): boolean {
  return Boolean(e.steps) || Object.keys(e.links).length > 0;
}

function isHttpUrl(value: string): boolean {
  try {
    const url = new URL(value);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
}

/**
 * Samma regler som `lessonUpdateSchema` i shared, men körda medan läraren
 * skriver så att felet hamnar vid rätt fält i stället för i ett 422-svar.
 * Servern validerar ändå — det här är bekvämlighet, inte skydd.
 */
function validate(form: LessonUpdateInput, status: 'draft' | 'published'): FormErrors {
  const links: Record<number, string> = {};

  form.links.forEach((link, i) => {
    const title = link.title.trim();
    const url = link.url.trim();
    // Helt tomma rader är en länk någon ångrade. De tas bort vid sparning i
    // stället för att bli ett fel att rätta.
    if (!title && !url) return;
    if (!url) links[i] = 'Skriv en adress, eller ta bort raden.';
    else if (!isHttpUrl(url)) links[i] = 'Adressen måste börja med http:// eller https://';
    else if (!title) links[i] = 'Länken behöver en titel som eleven förstår.';
  });

  const steps =
    status === 'published' && form.steps.every((s) => !s.trim())
      ? 'Lägg till minst ett steg innan du publicerar.'
      : undefined;

  return { steps, links };
}

/** Tomma rader bort och mellanslag trimmade, precis som schemat vill ha det. */
function cleaned(form: LessonUpdateInput): LessonUpdateInput {
  return {
    ...form,
    title: form.title.trim(),
    steps: form.steps.map((s) => s.trim()).filter(Boolean),
    helps: form.helps.map((h) => h.trim()).filter(Boolean),
    links: form.links
      .map((l) => ({ ...l, title: l.title.trim(), url: l.url.trim() }))
      .filter((l) => l.title !== '' || l.url !== ''),
    handInNote: form.handInNote.trim(),
  };
}

/* ------------------------------------------------------------------ */

interface Note {
  kind: 'ok' | 'error' | 'info';
  text: string;
}

interface RepeatableProps {
  idPrefix: string;
  legend: string;
  values: string[];
  placeholder: string;
  numbered?: boolean;
  error?: string;
  onChange: (values: string[]) => void;
}

function RepeatableList({
  idPrefix,
  legend,
  values,
  placeholder,
  numbered,
  error,
  onChange,
}: RepeatableProps) {
  const errorId = `${idPrefix}-error`;
  return (
    <fieldset className="fieldset">
      <legend className="label">{legend}</legend>
      {values.map((value, i) => (
        <div className="formrow" key={i}>
          {numbered && <span className="steps__num">{i + 1}</span>}
          <label className="visually-hidden" htmlFor={`${idPrefix}-${i}`}>
            {legend} rad {i + 1}
          </label>
          <input
            id={`${idPrefix}-${i}`}
            className="grow"
            type="text"
            value={value}
            aria-invalid={error ? true : undefined}
            aria-describedby={error ? errorId : undefined}
            onChange={(e) => {
              const next = [...values];
              next[i] = e.target.value;
              onChange(next);
            }}
          />
          <button
            type="button"
            className="iconbutton"
            aria-label={`Ta bort rad ${i + 1}`}
            onClick={() => onChange(values.filter((_, j) => j !== i))}
          >
            ×
          </button>
        </div>
      ))}
      {error && (
        <p className="field__error" id={errorId}>
          {error}
        </p>
      )}
      <button type="button" className="addrow" onClick={() => onChange([...values, ''])}>
        <IconPlus size={12} />
        {placeholder}
      </button>
    </fieldset>
  );
}

function toForm(lesson: LessonDetail): LessonUpdateInput {
  return {
    title: lesson.title ?? '',
    steps: lesson.steps.map((s) => s.text),
    helps: lesson.helps.map((h) => h.text),
    links: lesson.links.map((l) => ({
      title: l.title,
      url: l.url,
      sourceLabel: l.sourceLabel,
    })),
    book: {
      title: lesson.book.title ?? '',
      pages: lesson.book.pages ?? '',
      exercises: lesson.book.exercises ?? '',
    },
    handInNote: lesson.handInNote ?? '',
    submitMode: lesson.submitMode,
    visibleFrom: lesson.visibleFrom,
    chatEnabled: lesson.chatEnabled,
    chatMode: lesson.chatMode,
    status: lesson.status === 'empty' ? 'draft' : lesson.status,
  };
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}
